import { useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2 } from "lucide-react";

const blank = { name: "", email: "", company: "", phone: "", service_requested: "", details: "" };

export default function ServiceRequestDialog({ open, onOpenChange }) {
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.name || !form.email || !form.service_requested) { setErr("Please fill the required fields."); return; }
    setBusy(true);
    try {
      await api.post("/service-requests", form);
      setDone(true);
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Could not submit. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const close = (v) => { onOpenChange(v); if (!v) { setTimeout(() => { setDone(false); setForm(blank); setErr(""); }, 200); } };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="service-request-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-[#0D3B2D]">Request Services</DialogTitle>
          <DialogDescription className="text-[#4A635A]">Tell us what you need. Our team will review and contact you.</DialogDescription>
        </DialogHeader>
        {done ? (
          <div className="text-center py-6" data-testid="service-request-success">
            <CheckCircle2 className="h-12 w-12 text-[#10B981] mx-auto" />
            <h3 className="font-heading font-bold text-lg text-[#0D3B2D] mt-4">Request Received</h3>
            <p className="text-sm text-[#4A635A] mt-2">Thank you for your interest in ECOREXS. Our team will review your request and contact you regarding the next steps.</p>
            <Button onClick={() => close(false)} className="mt-5 bg-[#0D3B2D] text-white rounded-lg" data-testid="service-request-close">Close</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3 pt-1">
            {err && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{err}</div>}
            <div><Label>Full Name *</Label><Input value={form.name} onChange={set("name")} data-testid="sr-name" className="bg-white border-[#E2EBF0]" /></div>
            <div><Label>Email *</Label><Input type="email" value={form.email} onChange={set("email")} data-testid="sr-email" className="bg-white border-[#E2EBF0]" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Company</Label><Input value={form.company} onChange={set("company")} data-testid="sr-company" className="bg-white border-[#E2EBF0]" /></div>
              <div><Label>Phone</Label><Input value={form.phone} onChange={set("phone")} data-testid="sr-phone" className="bg-white border-[#E2EBF0]" /></div>
            </div>
            <div><Label>Service Requested *</Label><Input value={form.service_requested} onChange={set("service_requested")} data-testid="sr-service" className="bg-white border-[#E2EBF0]" placeholder="e.g. Website maintenance, consulting…" /></div>
            <div><Label>Additional Details</Label><Textarea value={form.details} onChange={set("details")} rows={3} data-testid="sr-details" className="bg-white border-[#E2EBF0]" /></div>
            <Button type="submit" disabled={busy} data-testid="sr-submit" className="w-full bg-[#67F2B3] hover:bg-[#4DE6A3] text-[#071A13] font-semibold rounded-lg">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit Request"}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
